import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatVisitRequestParams } from '../../chat.model';
import { combineDateTime, formatWhen, toDateInput, toTimeInput } from '../../chat.format';

interface SlotView {
  iso: string;
  label: string;
}

/**
 * `kind: 'visit_request'` (7.2): os horários propostos como botões e a resposta
 * do cliente (escolhido, proposto, sem disponibilidade). Para o staff
 * (`canSchedule`) um clique: "Confirmar visita" no horário escolhido, "Aceitar"
 * no proposto, "Marcar na mesma" quando o servidor não marcou por conflito de
 * agenda; e "Propor outro horário". `canAnswer` (responder em nome do cliente)
 * fica desligado no CRM.
 */
@Component({
  selector: 'chat-visit-request-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-visit-request-card.component.html',
  styleUrls: ['./chat-visit-request-card.component.css']
})
export class ChatVisitRequestCardComponent implements OnChanges {
  @Input() params: Partial<ChatVisitRequestParams> = {};
  @Input() mine = false;
  @Input() canAnswer = false;
  @Input() canSchedule = false;
  /** A marcação de um clique está a correr (spinner no botão). */
  @Input() busy = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() choose = new EventEmitter<string>();
  @Output() propose = new EventEmitter<string>();
  @Output() decline = new EventEmitter<void>();
  @Output() schedule = new EventEmitter<string>();
  @Output() proposeOther = new EventEmitter<void>();

  slots: SlotView[] = [];
  statusText = '';
  scheduleLabel = '';
  closed = false;
  scheduling = false;
  proposing = false;
  proposeDate = '';
  proposeTime = '';

  ngOnChanges(): void {
    const p = this.params || {};
    this.slots = (p.slots || []).map(iso => ({ iso, label: formatWhen(iso) }));
    this.scheduling = p.status === 'scheduling';
    this.closed = p.status === 'scheduled' || p.status === 'declined' || this.scheduling;
    const name = p.answeredByName || '';
    switch (p.status) {
      case 'chosen':
        this.statusText = (p.conflict ? this.strings.visitRequestConflict : this.strings.visitRequestChosen)
          .replace('{name}', name).replace('{when}', formatWhen(p.chosen));
        break;
      case 'proposed': this.statusText = this.strings.visitRequestProposed.replace('{name}', name).replace('{when}', formatWhen(p.proposedAt)); break;
      case 'declined': this.statusText = this.strings.visitRequestDeclined.replace('{name}', name); break;
      case 'scheduling': this.statusText = this.strings.visitRequestScheduling; break;
      case 'scheduled': this.statusText = this.strings.visitRequestScheduled; break;
      default: this.statusText = this.strings.visitRequestPending;
    }
    this.scheduleLabel = p.status === 'proposed' ? this.strings.acceptProposal
      : p.conflict ? this.strings.scheduleAnyway : this.strings.confirmVisitFromChoice;
  }

  /** O horário que o clique do staff marca: o escolhido, senão o proposto. */
  get pickedIso(): string | undefined {
    const p = this.params || {};
    return p.status === 'proposed' ? p.proposedAt : p.chosen;
  }

  startPropose(): void {
    const first = this.slots[0] ? new Date(this.slots[0].iso) : new Date();
    this.proposeDate = toDateInput(first);
    this.proposeTime = toTimeInput(first);
    this.proposing = true;
  }

  confirmPropose(): void {
    const when = combineDateTime(this.proposeDate, this.proposeTime);
    if (!when) {
      return;
    }
    this.proposing = false;
    this.propose.emit(when.toISOString());
  }

  trackBySlot = (_i: number, s: SlotView) => s.iso;
}
